import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Eye, EyeOff, Loader2, X, Calendar, ExternalLink,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SkeletonList } from "@/components/admin/Skeleton";

const empty = {
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  cover_image_url: "",
  tagsCsv: "",
  published: false,
};

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
}

export default function BlogPanel({ API, getAuthHeaders }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list; {} = new; {id,...} = edit
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/blog/posts`, { headers: getAuthHeaders() });
      setPosts(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => {
    setForm(empty);
    setEditing({});
  };

  const openEdit = async (p) => {
    try {
      const r = await axios.get(`${API}/admin/blog/posts/${p.id}`, { headers: getAuthHeaders() });
      const d = r.data;
      setForm({
        title: d.title,
        slug: d.slug,
        excerpt: d.excerpt || "",
        body: d.body || "",
        cover_image_url: d.cover_image_url || "",
        tagsCsv: (d.tags || []).join(", "),
        published: !!d.published,
      });
      setEditing(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load post");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        slug: form.slug.trim() || undefined,
        excerpt: form.excerpt.trim(),
        body: form.body,
        cover_image_url: form.cover_image_url.trim(),
        published: !!form.published,
        tags: form.tagsCsv.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editing && editing.id) {
        await axios.patch(`${API}/admin/blog/posts/${editing.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/admin/blog/posts`, payload, { headers: getAuthHeaders() });
        toast.success("Post created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (p) => {
    try {
      await axios.patch(`${API}/admin/blog/posts/${p.id}`, { published: !p.published }, { headers: getAuthHeaders() });
      toast.success(p.published ? "Unpublished" : "Published");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Toggle failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${p.id}`, { headers: getAuthHeaders() });
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-5" data-testid="blog-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-black uppercase tracking-wider text-white">Blog</h2>
          <p className="text-xs text-zinc-500 mt-1">Write SEO-friendly posts that show up at /blog.</p>
        </div>
        <button
          onClick={openNew}
          data-testid="blog-new-btn"
          className="btn-brutal btn-shine bg-red-600 text-white font-bold uppercase tracking-widest py-2.5 px-5 text-xs border-2 border-white hover:bg-white hover:text-black inline-flex items-center gap-2"
        >
          <Plus size={13} /> New Post
        </button>
      </div>

      {loading ? (
        <SkeletonList count={3} />
      ) : posts.length === 0 ? (
        <div className="border border-white/10 bg-[#0E0E0E] p-10 text-center">
          <p className="text-zinc-500 text-sm">No posts yet. Hit <strong className="text-white">New Post</strong> to write your first one.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {posts.map((p) => (
            <div key={p.id} data-testid={`blog-post-row-${p.id}`} className="border border-white/10 bg-[#0E0E0E] p-4 sm:p-5 hover:border-red-600/40 transition-colors">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-sm font-bold text-white truncate">{p.title}</h3>
                    {p.published ? (
                      <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-green-600/20 text-green-400 border border-green-600/40">Live</span>
                    ) : (
                      <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 bg-zinc-700/20 text-zinc-400 border border-zinc-600/40">Draft</span>
                    )}
                  </div>
                  <p className="text-[11px] text-zinc-500 mb-2 flex items-center gap-3 flex-wrap">
                    <span className="flex items-center gap-1"><Calendar size={10} /> {fmt(p.published_at || p.created_at)}</span>
                    <span className="font-mono truncate">/blog/{p.slug}</span>
                  </p>
                  {p.excerpt && <p className="text-xs text-zinc-400 line-clamp-2">{p.excerpt}</p>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {p.published && (
                    <a
                      href={`/blog/${p.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`blog-open-${p.id}`}
                      title="View live"
                      className="tap w-8 h-8 flex items-center justify-center border border-white/10 text-zinc-400 hover:text-red-500 hover:border-red-600/40 transition-colors"
                    >
                      <ExternalLink size={12} />
                    </a>
                  )}
                  <button
                    onClick={() => togglePublish(p)}
                    data-testid={`blog-toggle-${p.id}`}
                    title={p.published ? "Unpublish" : "Publish"}
                    className="tap w-8 h-8 flex items-center justify-center border border-white/10 text-zinc-400 hover:text-white hover:border-white/40 transition-colors"
                  >
                    {p.published ? <EyeOff size={12} /> : <Eye size={12} />}
                  </button>
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`blog-edit-${p.id}`}
                    title="Edit"
                    className="tap w-8 h-8 flex items-center justify-center border border-white/10 text-zinc-400 hover:text-white hover:border-white/40 transition-colors"
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    onClick={() => remove(p)}
                    data-testid={`blog-delete-${p.id}`}
                    title="Delete"
                    className="tap w-8 h-8 flex items-center justify-center border border-white/10 text-zinc-400 hover:text-red-500 hover:border-red-600/40 transition-colors"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0A0A0A] border border-white/10 rounded-none max-w-2xl sm:max-h-[90vh] overflow-y-auto" data-testid="blog-edit-modal">
          <DialogHeader>
            <DialogTitle className="text-sm font-black uppercase tracking-widest text-white">
              {editing?.id ? "Edit Post" : "New Post"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Title *</Label>
              <Input
                data-testid="blog-form-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="How to pick a web designer in India"
                className="mt-1 bg-[#141414] border-white/10 rounded-none focus:border-red-600 focus-visible:ring-0"
                required
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Slug (optional)</Label>
                <Input
                  data-testid="blog-form-slug"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="auto from title"
                  className="mt-1 bg-[#141414] border-white/10 rounded-none focus:border-red-600 focus-visible:ring-0 font-mono text-xs"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Tags (comma-separated)</Label>
                <Input
                  data-testid="blog-form-tags"
                  value={form.tagsCsv}
                  onChange={(e) => setForm({ ...form, tagsCsv: e.target.value })}
                  placeholder="seo, india, guide"
                  className="mt-1 bg-[#141414] border-white/10 rounded-none focus:border-red-600 focus-visible:ring-0"
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Cover Image URL</Label>
              <Input
                data-testid="blog-form-cover"
                value={form.cover_image_url}
                onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
                placeholder="https://..."
                className="mt-1 bg-[#141414] border-white/10 rounded-none focus:border-red-600 focus-visible:ring-0 font-mono text-xs"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Excerpt (shown on list)</Label>
              <Textarea
                data-testid="blog-form-excerpt"
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                rows={2}
                maxLength={400}
                placeholder="Short teaser for the card preview…"
                className="mt-1 bg-[#141414] border-white/10 rounded-none focus:border-red-600 focus-visible:ring-0 resize-none"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400">Body * (plain text, line-breaks preserved)</Label>
              <Textarea
                data-testid="blog-form-body"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={12}
                required
                placeholder="Write the full post here. Blank lines create paragraphs."
                className="mt-1 bg-[#141414] border-white/10 rounded-none focus:border-red-600 focus-visible:ring-0 resize-y font-mono text-xs"
              />
            </div>
            <div className="flex items-center justify-between gap-3 flex-wrap border-t border-white/10 pt-4">
              <label className="flex items-center gap-3 text-xs text-white cursor-pointer">
                <Switch
                  data-testid="blog-form-publish"
                  checked={form.published}
                  onCheckedChange={(v) => setForm({ ...form, published: v })}
                  className="data-[state=checked]:bg-red-600"
                />
                <span className="uppercase tracking-widest font-bold">{form.published ? "Publish live" : "Save as draft"}</span>
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setEditing(null)}
                  className="tap text-xs uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-2 inline-flex items-center gap-1"
                >
                  <X size={12} /> Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="blog-form-save"
                  className="btn-brutal btn-shine bg-red-600 text-white font-bold uppercase tracking-widest py-2.5 px-6 text-xs border-2 border-white hover:bg-white hover:text-black disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {saving && <Loader2 size={12} className="animate-spin" />}
                  {editing?.id ? "Save" : "Create"}
                </button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
